import { useForm, Link } from "@inertiajs/react";
import { useRef, useEffect, useState, useCallback } from "react";
import type Booze from "@/types/serializers/Booze";
import type BoozeImage from "@/types/serializers/BoozeImage";
import type Category from "@/types/serializers/Category";
import type Country from "@/types/serializers/Country";
import type Producer from "@/types/serializers/Producer";
import type Size from "@/types/serializers/Size";
import { useFlashToast } from "@/hooks/useFlashToast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  BoozeImageLabelEditor,
  BoozeImagePickerCombobox,
} from "@/components/BoozeImagePickerCombobox";
import { CountryCombobox } from "@/components/CountryCombobox";
import { ProducerCombobox } from "@/components/ProducerCombobox";
import { BoozeImageCropDialog } from "@/components/BoozeImageCropDialog";
import { ArrowLeft, X } from "lucide-react";

type Props = {
  booze: Booze;
  boozeImages: BoozeImage[];
  categories: Category[];
  countries: Country[];
  producers: Producer[];
  sizes: Size[];
  errors?: Record<string, string[]>;
  flash?: { alert?: string };
};

export default function BoozesNew({
  booze,
  boozeImages,
  categories,
  countries,
  producers,
  sizes,
  errors = {},
  flash,
}: Props) {
  useFlashToast(flash);

  const form = useForm({
    booze: {
      name: booze.name ?? "",
      description: booze.description ?? "",
      category_id: booze.category?.id ? String(booze.category.id) : "",
      country_id: booze.country?.id ? String(booze.country.id) : "",
      producer_id: booze.producer?.id ? String(booze.producer.id) : "",
      size_id: booze.size?.id ? String(booze.size.id) : "",
      booze_image_id: booze.boozeImageId != null ? String(booze.boozeImageId) : "",
      prior_booze_image_id: "",
      remove_image: false,
      new_image_label: "",
      image: null as File | null,
    },
  });

  const b = form.data.booze;
  const imagePreview = b.image ? URL.createObjectURL(b.image) : null;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [cropOpen, setCropOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [cropPending, setCropPending] = useState<{
    name: string;
    type: string;
  } | null>(null);
  const cropObjectUrlRef = useRef<string | null>(null);

  const releaseCropObjectUrl = useCallback(() => {
    if (cropObjectUrlRef.current) {
      URL.revokeObjectURL(cropObjectUrlRef.current);
      cropObjectUrlRef.current = null;
    }
    setCropImageSrc(null);
    setCropPending(null);
  }, []);

  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  useEffect(() => {
    return () => {
      releaseCropObjectUrl();
    };
  }, [releaseCropObjectUrl]);

  function handleProducerChange(value: string) {
    const producer = producers.find((p) => String(p.id) === value);
    const updates = { ...b, producer_id: value };
    if (producer?.country?.id) {
      updates.country_id = String(producer.country.id);
    }
    form.setData("booze", updates);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    form.post("/boozes", {
      forceFormData: true,
    });
  }

  function handleImageChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    e.target.value = "";
    if (!file) {
      return;
    }
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      return;
    }
    releaseCropObjectUrl();
    const url = URL.createObjectURL(file);
    cropObjectUrlRef.current = url;
    setCropImageSrc(url);
    setCropPending({ name: file.name, type: file.type || "image/jpeg" });
    setCropOpen(true);
  }

  function onCropDialogComplete(file: File) {
    releaseCropObjectUrl();
    setCropOpen(false);
    form.setData("booze", {
      ...form.data.booze,
      image: file,
      booze_image_id: "",
      remove_image: false,
    });
  }

  function handlePickLibraryImage(id: string) {
    setCropOpen(false);
    releaseCropObjectUrl();
    form.setData("booze", {
      ...b,
      booze_image_id: id,
      image: null,
      remove_image: false,
      new_image_label: "",
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function clearImage() {
    setCropOpen(false);
    releaseCropObjectUrl();
    form.setData("booze", {
      ...b,
      image: null,
      booze_image_id: "",
      remove_image: false,
      new_image_label: "",
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  return (
    <>
      {cropImageSrc && cropPending ? (
        <BoozeImageCropDialog
          open={cropOpen}
          imageSrc={cropImageSrc}
          sourceType={cropPending.type}
          sourceFileName={cropPending.name}
          onOpenChange={(o) => {
            if (!o) {
              releaseCropObjectUrl();
            }
            setCropOpen(o);
          }}
          onComplete={onCropDialogComplete}
        />
      ) : null}
    <div className="py-8 px-4">
      <div className="mx-auto max-w-xl space-y-6">
        <Button variant="ghost" size="sm" asChild>
          <Link href="/boozes">
            <ArrowLeft className="size-4" />
            Back to booze
          </Link>
        </Button>

        <Card>
          <CardHeader>
            <CardTitle>New Booze</CardTitle>
            <CardDescription>
              Add a new booze entry to your collection.
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="e.g. Eagle Rare 10 Year…"
                  value={b.name}
                  onChange={(e) =>
                    form.setData("booze", { ...b, name: e.target.value })
                  }
                  aria-invalid={!!errors.name}
                  autoFocus
                  required
                />
                {errors.name?.[0] && (
                  <p className="text-sm text-destructive">{errors.name[0]}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Optional tasting notes or description…"
                  value={b.description}
                  onChange={(e) =>
                    form.setData("booze", { ...b, description: e.target.value })
                  }
                  rows={3}
                  aria-invalid={!!errors.description}
                />
                {errors.description?.[0] && (
                  <p className="text-sm text-destructive">{errors.description[0]}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="image">Image</Label>
                <p className="text-xs text-muted-foreground">
                  Optional. Upload a new photo (you can crop before saving) or link one already used
                  on another bottle (same file, shared across entries).
                </p>
                <div className="space-y-2">
                  <span
                    id="booze_image_picker_label"
                    className="text-xs font-normal text-muted-foreground"
                  >
                    Link existing image
                  </span>
                  <BoozeImagePickerCombobox
                    key={boozeImages.map((i) => `${i.id}:${i.label ?? ""}`).join("|")}
                    images={boozeImages}
                    selectedId={b.booze_image_id}
                    onSelectId={handlePickLibraryImage}
                    disabled={!!b.image}
                    noneOptionLabel="None — upload below or skip"
                  />
                  {b.booze_image_id && !b.image ? (
                    <BoozeImageLabelEditor
                      imageId={b.booze_image_id}
                      images={boozeImages}
                      disabled={!!b.image}
                    />
                  ) : null}
                </div>
                <p className="text-xs font-medium text-muted-foreground">Or upload</p>
                {!b.image ? (
                  <div className="flex items-center gap-2">
                    <Input
                      ref={fileInputRef}
                      id="image"
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      onChange={handleImageChange}
                      disabled={!!b.booze_image_id}
                      aria-invalid={!!errors.image}
                      className="cursor-pointer"
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-3 rounded-md border border-input p-3">
                    {imagePreview && (
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="size-16 rounded object-cover"
                      />
                    )}
                    <span className="text-sm text-muted-foreground truncate flex-1">
                      {b.image.name}
                    </span>
                    <Button type="button" variant="ghost" size="icon" onClick={clearImage} title="Remove image">
                      <X className="size-4" />
                    </Button>
                  </div>
                )}
                {b.image ? (
                  <div className="space-y-2">
                    <Label htmlFor="new_image_label">Label for new image (optional)</Label>
                    <Input
                      id="new_image_label"
                      value={b.new_image_label}
                      onChange={(e) =>
                        form.setData("booze", {
                          ...b,
                          new_image_label: e.target.value,
                        })
                      }
                      maxLength={255}
                      placeholder="Short tag for finding this photo again…"
                      autoComplete="off"
                    />
                  </div>
                ) : null}
                {errors.image?.[0] && (
                  <p className="text-sm text-destructive">{errors.image[0]}</p>
                )}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="category_id">Category</Label>
                  <Select
                    value={b.category_id}
                    onValueChange={(value) =>
                      form.setData("booze", { ...b, category_id: value })
                    }
                  >
                    <SelectTrigger id="category_id" aria-invalid={!!errors.category}>
                      <SelectValue placeholder="Select a category…" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={String(cat.id)}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.category?.[0] && (
                    <p className="text-sm text-destructive">{errors.category[0]}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="producer_id">Producer</Label>
                  <ProducerCombobox
                    id="producer_id"
                    producers={producers}
                    value={b.producer_id}
                    onValueChange={handleProducerChange}
                    aria-invalid={!!errors.producer}
                  />
                  {errors.producer?.[0] && (
                    <p className="text-sm text-destructive">{errors.producer[0]}</p>
                  )}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="country_id">Country</Label>
                  <CountryCombobox
                    id="country_id"
                    countries={countries}
                    value={b.country_id}
                    onValueChange={(value) =>
                      form.setData("booze", { ...b, country_id: value })
                    }
                    aria-invalid={!!errors.country}
                  />
                  {errors.country?.[0] && (
                    <p className="text-sm text-destructive">{errors.country[0]}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="size_id">Size</Label>
                  <Select
                    value={b.size_id}
                    onValueChange={(value) =>
                      form.setData("booze", { ...b, size_id: value })
                    }
                  >
                    <SelectTrigger id="size_id" aria-invalid={!!errors.size}>
                      <SelectValue placeholder="Select a size…" />
                    </SelectTrigger>
                    <SelectContent>
                      {sizes.map((size) => (
                        <SelectItem key={size.id} value={String(size.id)}>
                          {size.volume} {size.measurementType}{size.nickname ? ` (${size.nickname})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.size?.[0] && (
                    <p className="text-sm text-destructive">{errors.size[0]}</p>
                  )}
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline" asChild>
                <Link href="/boozes">Cancel</Link>
              </Button>
              <Button type="submit" disabled={form.processing}>
                {form.processing ? "Creating…" : "Create Booze"}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
    </>
  );
}
